import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom'; 
import { Link } from 'react-router-dom';
import axios from 'axios'; 
import { 
  ArrowLeft, 
  Search, 
  Plus, 
  Edit2, 
  Trash2, 
  Building2, 
  X,
  XCircle,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ShieldAlert // Icon cho màn hình chặn truy cập
} from 'lucide-react';

// Sử dụng biến môi trường từ file .env, có dự phòng fallback
const API_BASE = process.env.REACT_APP_API_URL || "http://172.16.20.212:8001/api";

const Facilities = () => {
  // --- STATE QUẢN LÝ DỮ LIỆU ---
  const [facilities, setFacilities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // --- STATE KIỂM TRA QUYỀN TRUY CẬP ---
  const [isAuthorized, setIsAuthorized] = useState(true);

  // --- STATE QUẢN LÝ THÔNG BÁO (TOAST) ---
  const [toastMessage, setToastMessage] = useState(null);

  // --- STATE QUẢN LÝ MODAL & FORM ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // --- STATE QUẢN LÝ MODAL XÓA ---
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null, name: '' });
  
  const [formData, setFormData] = useState({
    ma_cskcb: '', ten_cskcb: '', cap_cmkt: 'Cấp cơ bản'
  });

  const API_URL = `${API_BASE}/facilities`; // Nối đường dẫn API từ base URL

  // Hàm hiển thị Toast
  const showToast = (message, isError = false) => {
    setToastMessage({ text: message, isError });
    setTimeout(() => setToastMessage(null), 3500);
  };

  // --- HÀM LOAD DỮ LIỆU TỪ SERVER ---
  const fetchFacilities = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // 1. KIỂM TRA ROLE TỪ user_info TRONG LOCAL STORAGE
      const userInfoStr = localStorage.getItem('user_info');
      
      if (!userInfoStr) {
        // Nếu không có thông tin user, chặn luôn
        setIsAuthorized(false);
        setIsLoading(false);
        return;
      }

      try {
        const userInfo = JSON.parse(userInfoStr);
        // Nếu role là 'user' hoặc không phải 'admin' -> Chặn
        if (userInfo.role && userInfo.role.toLowerCase() !== 'admin') {
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }
      } catch (parseError) {
        console.error("Lỗi phân tích dữ liệu user_info:", parseError);
        setIsAuthorized(false);
        setIsLoading(false);
        return;
      }

      // 2. GỌI API NẾU ĐÃ XÁC NHẬN LÀ ADMIN
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data.data || response.data;
      setFacilities(data);
    } catch (err) {
      console.error("Lỗi khi tải danh sách cơ sở:", err);
      // BẮT LỖI 403 TỪ BACKEND NẾU TOKEN KHÔNG HỢP LỆ
      if (err.response && (err.response.status === 403 || err.response.status === 401)) {
        setIsAuthorized(false);
      } else {
        setError('Không thể kết nối đến máy chủ. Vui lòng thử lại sau.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFacilities();
  }, []);

  // --- LỌC TÌM KIẾM ---
  const filteredFacilities = facilities.filter(f => 
    (f.ten_cskcb && f.ten_cskcb.toLowerCase().includes(searchTerm.toLowerCase())) || 
    (f.ma_cskcb && f.ma_cskcb.includes(searchTerm))
  );

  // --- XỬ LÝ SỰ KIỆN NÚT BẤM ---
  const handleAddNew = () => {
    setEditingItem(null);
    setFormData({ ma_cskcb: '', ten_cskcb: '', cap_cmkt: 'Cấp cơ bản' });
    setIsModalOpen(true);
  };

  const handleEdit = (facility) => {
    setEditingItem(facility);
    setFormData({ 
      ma_cskcb: facility.ma_cskcb, 
      ten_cskcb: facility.ten_cskcb, 
      cap_cmkt: facility.cap_cmkt || 'Cấp cơ bản' 
    });
    setIsModalOpen(true);
  };

  // Kích hoạt Modal Xóa
  const handleDeleteClick = (facility) => {
    setDeleteModal({ isOpen: true, id: facility.id, name: facility.ten_cskcb });
  };

  // Xác nhận Xóa thực sự
  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API_URL}/${deleteModal.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFacilities(facilities.filter(f => f.id !== deleteModal.id));
      showToast("Đã xóa cơ sở KCB thành công!");
    } catch (err) {
      console.error("Lỗi xóa cơ sở:", err);
      showToast(err.response?.data?.message || err.response?.data?.detail || "Có lỗi xảy ra khi xóa.", true);
    } finally {
      setDeleteModal({ isOpen: false, id: null, name: '' });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };

      if (editingItem) {
        const response = await axios.put(`${API_URL}/${editingItem.id}`, formData, { headers });
        const updatedItem = response.data.data || response.data;
        setFacilities(facilities.map(f => f.id === editingItem.id ? updatedItem : f));
        showToast("Cập nhật cơ sở thành công!");
      } else {
        const response = await axios.post(API_URL, formData, { headers });
        const newItem = response.data.data || response.data;
        setFacilities([newItem, ...facilities]);
        showToast("Thêm mới cơ sở thành công!");
      }
      setIsModalOpen(false);
    } catch (err) {
      console.error("Lỗi lưu cơ sở:", err);
      showToast(err.response?.data?.message || err.response?.data?.detail || "Không thể lưu dữ liệu.", true);
    } finally {
      setIsSaving(false);
    }
  };

  // ================= MÀN HÌNH CHẶN TRUY CẬP =================
  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8 flex items-center justify-center">
        <div className="bg-white p-8 md:p-12 rounded-[2rem] shadow-xl text-center max-w-md border border-slate-100 animate-in zoom-in-95 duration-300">
          <div className="w-20 h-20 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShieldAlert size={40} className="text-rose-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-3">Truy cập bị từ chối</h2>
          <p className="text-slate-500 mb-8 text-sm leading-relaxed">
            Bạn không có quyền quản trị (Role Admin) để xem và chỉnh sửa danh sách cơ sở khám chữa bệnh. Vui lòng liên hệ quản trị viên.
          </p>
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-slate-800 text-white text-sm font-bold rounded-xl hover:bg-slate-700 transition-colors shadow-sm"
          >
            <ArrowLeft size={16} /> Quay lại trang chủ
          </Link>
        </div>
      </div>
    );
  }

  // ================= MÀN HÌNH CHÍNH =================
  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8 relative">
      
      {/* ================= TOAST THÔNG BÁO ================= */}
      {toastMessage && createPortal(
        <div className={`fixed bottom-10 right-1/2 translate-x-1/2 lg:translate-x-0 lg:right-8 z-[9999] text-white px-5 py-3.5 rounded-2xl shadow-2xl flex items-center gap-3 animate-slide-up-fade whitespace-nowrap ${toastMessage.isError ? 'bg-rose-600' : 'bg-slate-800'}`}>
          {toastMessage.isError ? <AlertCircle size={18} className="text-rose-200" /> : <CheckCircle2 size={18} className="text-emerald-400" />}
          <span className="text-sm font-bold">{toastMessage.text}</span>
        </div>,
        document.body
      )}

      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Link to="/settings" className="p-2 bg-white rounded-xl shadow-sm text-slate-500 hover:text-blue-600 transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Building2 className="text-cyan-600" />
                Quản lý Cơ sở KCB
              </h1>
              <p className="text-sm text-slate-500">Danh sách các cơ sở khám chữa bệnh trên hệ thống</p>
            </div>
          </div>
          
          <button 
            onClick={handleAddNew}
            className="flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white px-5 py-2.5 rounded-xl font-medium transition-colors shadow-sm shadow-cyan-200"
          >
            <Plus size={18} />
            <span>Thêm Cơ Sở Mới</span>
          </button>
        </div>

        <div className="bg-white rounded-[2rem] p-6 border border-slate-100 shadow-sm">
          <div className="flex items-center bg-slate-50 rounded-xl px-4 py-3 mb-6 border border-slate-100 focus-within:border-cyan-400 focus-within:ring-2 focus-within:ring-cyan-100 transition-all">
            <Search className="text-slate-400 mr-3" size={20} />
            <input 
              type="text" 
              placeholder="Tìm kiếm theo mã KCB hoặc tên cơ sở..." 
              className="bg-transparent border-none outline-none w-full text-slate-700 placeholder:text-slate-400"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-cyan-500 animate-spin mb-4" />
              <p className="text-slate-500 text-sm">Đang tải dữ liệu cơ sở KCB...</p>
            </div>
          ) : error ? (
            <div className="py-8 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <p className="text-red-600 font-medium">{error}</p>
              <button 
                onClick={fetchFacilities}
                className="mt-4 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
              >
                Thử lại
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-100 text-slate-400 text-xs uppercase tracking-wider">
                    <th className="pb-3 font-medium">Mã KCB</th>
                    <th className="pb-3 font-medium">Tên Cơ Sở</th>
                    <th className="pb-3 font-medium">Phân cấp CMKT</th>
                    <th className="pb-3 font-medium text-right">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {filteredFacilities.map((facility) => (
                    <tr key={facility.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors group">
                      <td className="py-4 font-semibold text-slate-700">{facility.ma_cskcb}</td>
                      <td className="py-4 font-medium text-slate-800">
                        {facility.ten_cskcb}
                      </td>
                      <td className="py-4 text-slate-600">
                        <span className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-100 rounded-lg text-xs font-medium">
                          {facility.cap_cmkt || 'Chưa phân cấp'}
                        </span>
                      </td>
                      <td className="py-4 text-right">
                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => handleEdit(facility)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                            <Edit2 size={16} />
                          </button>
                          <button onClick={() => handleDeleteClick(facility)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredFacilities.length === 0 && (
                    <tr>
                      <td colSpan="4" className="py-12 text-center text-slate-400">
                        Không tìm thấy cơ sở nào phù hợp.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ================= MODAL THÊM / SỬA ================= */}
        {isModalOpen && createPortal(
          <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-3xl shadow-xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <h3 className="text-lg font-bold text-slate-800">
                  {editingItem ? 'Cập nhật Cơ sở KCB' : 'Thêm mới Cơ sở KCB'}
                </h3>
                <button 
                  onClick={() => setIsModalOpen(false)} 
                  disabled={isSaving}
                  className="text-slate-400 hover:text-slate-600 p-1 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors disabled:opacity-50"
                >
                  <X size={20} />
                </button>
              </div>
              
              <form onSubmit={handleSave} className="p-6">
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wide">Mã KCB *</label>
                      <input 
                        type="text" 
                        required
                        placeholder="VD: 94019"
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 transition-all text-slate-700 font-medium"
                        value={formData.ma_cskcb}
                        onChange={(e) => setFormData({...formData, ma_cskcb: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wide">Phân Cấp CMKT</label>
                      <select 
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 transition-all text-slate-700"
                        value={formData.cap_cmkt}
                        onChange={(e) => setFormData({...formData, cap_cmkt: e.target.value})}
                      >
                        <option value="Cấp ban đầu">Cấp ban đầu</option>
                        <option value="Cấp cơ bản">Cấp cơ bản</option>
                        <option value="Cấp chuyên sâu">Cấp chuyên sâu</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wide">Tên Cơ Sở *</label>
                    <input 
                      type="text" 
                      required
                      placeholder="VD: Bệnh viện Đa khoa Hoàng Tuấn"
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 transition-all text-slate-700"
                      value={formData.ten_cskcb}
                      onChange={(e) => setFormData({...formData, ten_cskcb: e.target.value})}
                    />
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-end gap-3">
                  <button 
                    type="button" 
                    onClick={() => setIsModalOpen(false)}
                    disabled={isSaving}
                    className="px-5 py-2.5 text-sm font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors disabled:opacity-50"
                  >
                    Hủy bỏ
                  </button>
                  <button 
                    type="submit" 
                    disabled={isSaving}
                    className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-cyan-600 hover:bg-cyan-700 rounded-xl shadow-sm shadow-cyan-200 transition-colors disabled:opacity-50"
                  >
                    {isSaving && <Loader2 size={16} className="animate-spin" />}
                    {editingItem ? 'Lưu thay đổi' : 'Thêm mới'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body 
        )}

        {/* ================= MODAL XÁC NHẬN XÓA ================= */}
        {deleteModal.isOpen && createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-3xl shadow-xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200 text-center p-6 border border-slate-100">
              <div className="w-16 h-16 bg-rose-100 text-rose-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Xác nhận xóa</h3>
              <p className="text-sm text-slate-500 mb-6">
                Bạn có chắc chắn muốn xóa cơ sở <br/>
                <strong className="text-slate-800">{deleteModal.name}</strong>?<br/> 
                Thao tác này không thể hoàn tác!
              </p>
              <div className="flex gap-3">
                <button 
                  onClick={() => setDeleteModal({ isOpen: false, id: null, name: '' })}
                  className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-bold rounded-xl transition-colors"
                >
                  Hủy bỏ
                </button>
                <button 
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold rounded-xl shadow-sm shadow-rose-200 transition-colors"
                >
                  Xóa ngay
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
        
      </div>

      {/* --- CSS Animations cho Toast --- */}
      <style>{`
        @keyframes slideUpFade {
          from { opacity: 0; transform: translateY(20px) translateX(-50%) scale(0.95); }
          to { opacity: 1; transform: translateY(0) translateX(-50%) scale(1); }
        }
        @media (min-width: 1024px) {
          @keyframes slideUpFade {
            from { opacity: 0; transform: translateY(20px) scale(0.95); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
        }
        .animate-slide-up-fade { animation: slideUpFade 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
      `}</style>
    </div>
  );
};

export default Facilities;